import React, { useEffect, useRef } from 'react';
import propTypes from 'prop-types';

import { useMap, useDeviceType } from '../../../hooks';
import DealerMap from './../../../lib/dealer-map';

import Dialog from '../../Dialog';
import GeolocationButton from './../../GeolocationButton';
import MapOverlay from './MapOverlay';
import { useTranslation } from 'react-i18next';
import MapRevokeConsentButton from './MapRevokeConsentButton';

/**
 * Wrapper component for dealer map. Map is stored in context and controlled from Dealers component.
 *
 * @return {React.ReactNode}
 */
function Map({ initialDealer, location, userLocation, onUserLocationChange }) {
  //console.log('Map rendered');
  const { t } = useTranslation();
  const { isMobile } = useDeviceType();
  const { map, setMap, googleMapsConsent, googleMapsReady, mapEnabled, mapVisible, setMapVisible } = useMap();
  const mapRef = useRef(null);
  const mobileMapRef = useRef(null);

  function handleCloseMap() {
    if (isMobile) {
      setMapVisible(false);
    }
  }

  // Initializes map
  useEffect(() => {
    if (!mapEnabled || !googleMapsReady || !location) {
      return;
    }
    const containerId = 'react-map-container';

    if (!googleMapsConsent) {
      if (map) {
        map.destroy();
        setMap(null);
        document.getElementById(containerId)?.remove();
        console.log('Google Maps instance removed');

      }
      return;
    }

    const containerEl = isMobile ? mobileMapRef.current : mapRef.current;

    if (map) {
      const mapEl = map.getMap().getDiv();
      if (mapEl.parentElement !== containerEl) {
        containerEl.appendChild(mapEl);
        console.log('Google Maps instance moved to another element', containerEl);
      }

      return;
    }

    const container = document.createElement('div');
    container.id = containerId;
    container.classList.add('react-map-container');
    containerEl.appendChild(container);
    const newMap = new DealerMap({
      zoom: 11,
      element: container,
      userLocation: userLocation || null,
      centerLocation: location ? { lat: location.lat, lng: location.lng } : null,
      i18n: {
        clusterOfMarkers: t('Cluster of markers'),
      }
    });
    setMap(newMap);
    console.log('Google Maps instance initialized', { location, userLocation });
  }, [map, mapEnabled, googleMapsConsent, googleMapsReady, isMobile, location, userLocation]);

  // Set map settings
  useEffect(() => {
    if (map) {
      map.getMap().setOptions({
        mapTypeControl: !isMobile,
        fullscreenControl: !isMobile,
        streetViewControl: !isMobile
      });
    }
  }, [map, isMobile]);

  // Toggle map visibility when device type changes
  useEffect(() => {
    setMapVisible(isMobile ? false : true);
  }, [isMobile]);


  // Set user location
  useEffect(() => {
    if (!map || !userLocation) {
      return;
    }

    map.setUserLocation(userLocation);
  }, [map, userLocation]);

  return (
    <>
      <div className={'react-map react-map--desktop' + (mapVisible ? ' react-map--visible' : '') + (mapEnabled ? ' react-map--enabled' : '')}>
        <div className="map-el map-el--desktop" ref={mapRef} hidden={!mapVisible}></div>
        <MapOverlay
          initialDealer={initialDealer}
          location={location}
          userLocation={userLocation}
          onUserLocationChange={onUserLocationChange}
        />
        {['session', 'local'].includes(googleMapsConsent) && (
          <MapRevokeConsentButton />
        )}
      </div>

      <Dialog id="dealer-map-popover" type="map" open={isMobile && mapVisible} direction={'left'} onClose={handleCloseMap}>
        <button data-close-map="dealer-map-popover" className="close-map-modal" aria-label={t('Close map')} onClick={handleCloseMap}>&lsaquo;</button>
        {map && (
          <GeolocationButton onLocate={onUserLocationChange} />
        )}

        <div className={'react-map react-map--mobile' + (mapVisible ? ' react-map--visible' : '') + (mapEnabled ? ' react-map--enabled' : '')}>
          <div className="map-el map-el--mobile" ref={mobileMapRef}></div>
          <MapOverlay
            initialDealer={initialDealer}
            location={location}
            userLocation={userLocation}
            onUserLocationChange={onUserLocationChange}
          />
          {['session', 'local'].includes(googleMapsConsent) && (
            <MapRevokeConsentButton />
          )}
        </div>
      </Dialog>
    </>
  );
}

Map.propTypes = {
  initialDealer: propTypes.object,
  location: propTypes.object,
  userLocation: propTypes.object,
  onUserLocationChange: propTypes.func,
};

export default Map;
